/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
    Box, Card, CardContent, TextField, Button, Typography,
    InputAdornment, IconButton, Link,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Alert, useMediaQuery, useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { navLogoUrl } from './brandAssets';

function LoginPage() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const theme = useTheme();
    const isWide = useMediaQuery(theme.breakpoints.up('md'));

    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotSubmitted, setForgotSubmitted] = useState(false);
    const [forgotError, setForgotError] = useState('');
    const [forgotSubmitting, setForgotSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (error) setError('');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!formData.email.trim() || !formData.password.trim()) {
            setError('Please enter both email and password.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const baseUrl = process.env.REACT_APP_CONNECTOR_API_URL;
            const res = await fetch(`${baseUrl}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: formData.email, password: formData.password }),
            });

            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setError(body.error || 'Invalid email or password.');
                return;
            }

            const data = await res.json();
            login(data.token, data.role, data.userId);

            if (data.role === 'admin') {
                navigate('/dashboard');
            } else {
                navigate(`/settings/${data.userId}`);
            }
        } catch {
            setError('Unable to reach the server. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        if (!forgotEmail.trim()) {
            setForgotError('Please enter your email address.');
            return;
        }

        setForgotSubmitting(true);
        setForgotError('');

        try {
            const baseUrl = process.env.REACT_APP_CONNECTOR_API_URL;
            await fetch(`${baseUrl}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail }),
            });
            // The API always responds the same way whether or not the email is
            // registered, so the UI shows the same confirmation regardless.
            setForgotSubmitted(true);
        } catch {
            setForgotError('Unable to reach the server. Please try again.');
        } finally {
            setForgotSubmitting(false);
        }
    };

    const handleForgotClose = () => {
        setForgotOpen(false);
        setForgotEmail('');
        setForgotSubmitted(false);
        setForgotError('');
    };

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: '#fafbfc',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' },
            '&.Mui-focused fieldset': { borderColor: 'var(--zamp-color-app-bar)' },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: 'var(--zamp-color-app-bar)',
        },
    };

    const welcomePanel = (
        <Box
            sx={{
                flex: isWide ? '0 0 50%' : 'none',
                minHeight: isWide ? '100vh' : 280,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 4,
                py: 6,
                background: `linear-gradient(145deg, #2a5f5d 0%, var(--zamp-color-app-bar) 45%, #0f2827 100%)`,
                color: 'var(--zamp-color-app-bar-text)',
                textAlign: 'center',
            }}
        >
            <Box
                component="img"
                src={navLogoUrl}
                alt="Zamp"
                sx={{ height: { xs: 28, md: 36 }, width: 'auto', mb: 4 }}
            />
            <Typography
                component="h1"
                sx={{
                    fontFamily: 'var(--zamp-font-headline) !important',
                    fontSize: { xs: '2rem', md: '2.75rem' },
                    fontWeight: 600,
                    letterSpacing: 'var(--zamp-tracking-headline)',
                    lineHeight: 1.15,
                    mb: 2,
                }}
            >
                Welcome back
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    maxWidth: 360,
                    opacity: 0.9,
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    lineHeight: 1.6,
                }}
            >
                Sign in to access your dashboard, settings, and tax sync tools.
            </Typography>
        </Box>
    );

    const loginForm = (
        <Box
            sx={{
                flex: isWide ? '0 0 50%' : 1,
                minHeight: isWide ? '100vh' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f0f4f3',
                px: { xs: 2, sm: 4 },
                py: { xs: 4, md: 6 },
            }}
        >
            <Card
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(24, 62, 61, 0.1)',
                    border: '1px solid rgba(24, 62, 61, 0.06)',
                }}
            >
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    <Typography
                        component="h2"
                        align="center"
                        sx={{
                            fontFamily: 'var(--zamp-font-headline) !important',
                            fontSize: '1.35rem',
                            fontWeight: 600,
                            color: '#1e293b',
                            mb: 3,
                            letterSpacing: 'var(--zamp-tracking-headline)',
                        }}
                    >
                        Login to your account
                    </Typography>

                    <Box component="form" onSubmit={handleLogin} noValidate>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Typography
                            component="label"
                            htmlFor="login-email"
                            variant="body2"
                            sx={{ display: 'block', fontWeight: 500, color: '#475569', mb: 0.75 }}
                        >
                            Email
                        </Typography>
                        <TextField
                            id="login-email"
                            fullWidth
                            name="email"
                            type="email"
                            placeholder="Enter your email"
                            value={formData.email}
                            onChange={handleChange}
                            autoComplete="email"
                            autoFocus
                            hiddenLabel
                            size="small"
                            sx={{ ...inputSx, mb: 2 }}
                        />

                        <Typography
                            component="label"
                            htmlFor="login-password"
                            variant="body2"
                            sx={{ display: 'block', fontWeight: 500, color: '#475569', mb: 0.75 }}
                        >
                            Password
                        </Typography>
                        <TextField
                            id="login-password"
                            fullWidth
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            value={formData.password}
                            onChange={handleChange}
                            autoComplete="current-password"
                            hiddenLabel
                            size="small"
                            sx={{ ...inputSx, mb: 1 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showPassword
                                                ? <VisibilityOff fontSize="small" />
                                                : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ textAlign: 'right', mt: 1, mb: 3 }}>
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() => setForgotOpen(true)}
                                underline="hover"
                                sx={{
                                    color: 'var(--zamp-color-button-primary)',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                }}
                            >
                                Forgot password?
                            </Link>
                        </Box>

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={loading}
                            sx={{
                                py: 1.4,
                                borderRadius: 2,
                                fontWeight: 600,
                                textTransform: 'none',
                                fontSize: '1rem',
                                boxShadow: 'none',
                                '&:hover': {
                                    boxShadow: '0 4px 14px rgba(24, 62, 61, 0.35)',
                                },
                            }}
                        >
                            {loading ? 'Signing in…' : 'Login'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );

    return (
        <>
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: isWide ? 'row' : 'column',
                }}
            >
                {welcomePanel}
                {loginForm}
            </Box>

            <Dialog
                open={forgotOpen}
                onClose={handleForgotClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 0.5 }}>
                    <Typography variant="h6" fontWeight={700}>Reset Password</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Enter your email and we'll send you a reset link.
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    {forgotSubmitted ? (
                        <Alert severity="success" sx={{ mt: 1, borderRadius: 2 }}>
                            If an account with that email exists, a password reset link has been sent. Please check your inbox.
                        </Alert>
                    ) : (
                        <Box component="form" onSubmit={handleForgotSubmit} noValidate>
                            {forgotError && (
                                <Alert severity="error" sx={{ mb: 2, mt: 1, borderRadius: 2 }}>
                                    {forgotError}
                                </Alert>
                            )}
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={forgotEmail}
                                onChange={(e) => {
                                    setForgotEmail(e.target.value);
                                    if (forgotError) setForgotError('');
                                }}
                                autoFocus
                                margin="normal"
                                sx={inputSx}
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={handleForgotClose}
                        variant="outlined"
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        {forgotSubmitted ? 'Close' : 'Cancel'}
                    </Button>
                    {!forgotSubmitted && (
                        <Button
                            onClick={handleForgotSubmit}
                            variant="contained"
                            color="primary"
                            disabled={forgotSubmitting}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            {forgotSubmitting ? 'Sending…' : 'Send Reset Link'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

export default LoginPage;
